/* eslint-disable react-hooks/exhaustive-deps */
// Node modules.
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { debounce } from 'lodash-es';
// Relative imports.
import { langSelectedAction } from 'applications/static-pages/i18Select/actions';
import CrisisPanel from './CrisisPanel';
import DesktopLinks from './DesktopLinks';
import LanguageSupport from './LanguageSupport';
import MobileLinks from './MobileLinks';
import { createLinkGroups } from '../helpers';
import * as accessibilityUtils from '../../../utilities/accessibility/index';
import { replaceWithStagingDomain } from '../../../utilities/environment/stagingDomains';
import { toggleMinimalFooter } from '../../header/helpers';
import FEATURE_FLAG_NAMES from '../../../utilities/feature-toggles/featureFlagNames';

const Footer = ({
  footerData,
  showMinimalFooter,
  dispatchLanguageSelection,
  languageCode,
  onFooterLoad,
}) => {
  const path = useSelector(state => state?.navigation?.route?.path);
  // Rolls out and back with the new header, whose logo also links to My VA.
  const sealLinksToMyVa = useSelector(
    state =>
      state?.user?.login?.currentlyLoggedIn &&
      state?.featureToggles?.[FEATURE_FLAG_NAMES.myVaBlueskyEnabled],
  );
  const linkObj = useMemo(() => createLinkGroups(footerData), []);
  const [isMobile, setIsMobile] = useState(!accessibilityUtils.isWideScreen());
  const [isMinimalFooter, setIsMinimalFooter] = useState(
    typeof showMinimalFooter === 'function'
      ? showMinimalFooter(path)
      : showMinimalFooter,
  );

  useEffect(() => {
    const onResize = debounce(() => {
      setIsMobile(!accessibilityUtils.isWideScreen());
    }, 250);

    window.addEventListener('resize', onResize);
    onFooterLoad?.();

    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setIsMinimalFooter(
      typeof showMinimalFooter === 'function'
        ? showMinimalFooter(path)
        : showMinimalFooter,
    );
  }, [showMinimalFooter, path]);

  useEffect(() => {
    toggleMinimalFooter(isMinimalFooter);
  }, [isMinimalFooter]);

  return (
    <div>
      <div className="footer-inner">
        <DesktopLinks visible={!isMinimalFooter && !isMobile} links={linkObj} />
        <MobileLinks
          visible={isMobile}
          links={linkObj}
          langConfig={{
            dispatchLanguageSelection,
            languageCode,
          }}
          minimalFooter={isMinimalFooter}
        />
        {!isMinimalFooter && !isMobile && (
          <LanguageSupport
            isDesktop
            dispatchLanguageSelection={dispatchLanguageSelection}
            languageCode={languageCode}
          />
        )}

        <div className="usa-grid usa-grid-full footer-banner">
          <a href={sealLinksToMyVa ? '/my-va/' : '/'}>
            <img
              src={replaceWithStagingDomain(
                'https://www.va.gov/img/homepage/va-logo-white.png',
              )}
              alt={
                sealLinksToMyVa ? 'Go to your My VA dashboard' : 'Go to VA.gov'
              }
              width="200"
              className="vads-u-height--auto"
            />
          </a>
        </div>
        {!isMinimalFooter && (
          <div className="usa-grid usa-grid-full va-footer-links-bottom">
            {linkObj.bottomLinks}
          </div>
        )}
      </div>
      <CrisisPanel />
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchLanguageSelection: lang => dispatch(langSelectedAction(lang)),
});

const mapStateToProps = state => ({
  languageCode: state.i18State.lang,
});

Footer.propTypes = {
  footerData: PropTypes.arrayOf(PropTypes.object).isRequired,
  dispatchLanguageSelection: PropTypes.func,
  languageCode: PropTypes.string,
  showMinimalFooter: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onFooterLoad: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
